// Make changes — the ⚙ panel of one button on the record's action bar (or
// "+ Add a button"). Name, what it does, where people see it, when it shows,
// and whether it asks "Are you sure?" first — compiled to the designer's
// ActionDef by ./build/actions. The panel holds its changes and Done saves
// them as ONE step (a new button = an insert; a changed one = remove + insert
// in the same place, so a rename and its undo stay exact).

import { useEffect, useMemo, useState } from 'react'
import type { ActionDef, ColumnDef, SqlModel, TransactionModel } from '../txn/types'
import { legacyActionToDef } from '../txn/types'
import type { CustomizeSession } from './session'
import type { BuildOps, BuildSources } from './build/types'
import { formBase, truthy } from './txnOps'
import {
  AnchoredPanel, ChipGroup, PanelHeader, PanelNote, PanelRow, PanelSelect, Switch, VIOLET, VioletButton,
} from './ui'
import { BUTTON_DOES, actionToSpec, buttonSentence, specToAction, type ButtonDoes, type ButtonSpec } from './build/actions'
import { parseLookup } from './build/fieldKinds'
import { tw } from '../testHooks'
import { Loader2, Trash2 } from 'lucide-react'

export interface ButtonPanelProps {
  session: CustomizeSession
  /** The screen's (effective) transaction model. */
  model: TransactionModel
  objName: string
  /** The form the button is on (its form_no). */
  scopeKey: string
  /** The button's name; null = a new button. */
  name: string | null
  anchor: HTMLElement | null
  transport: BuildOps
  onClose: () => void
}

const unwrapForm = (s: unknown): SqlModel => ((s as { sql_model?: SqlModel })?.sql_model ?? s) as SqlModel

export default function ButtonPanel({ session, model, objName, scopeKey, name, anchor, transport, onClose }: ButtonPanelProps) {
  const forms = (model.sql_models || []).map(unwrapForm)
  const form = forms.find((f) => String(f.form_no ?? '1') === scopeKey) || forms[0]
  const formNo = String(form?.form_no ?? '1')
  // Where the button lives: the form's own list, or the transaction's.
  const located = useMemo(() => {
    if (!name) return null
    const own = (form?.actions || []).map(legacyActionToDef)
    const i = own.findIndex((a) => a.name === name)
    if (i >= 0) return { array: `${formBase(formNo)}.actions`, list: own, index: i }
    const txn = ((model as { actions?: unknown[] }).actions || []).map((a) => legacyActionToDef(a as never))
    const j = txn.findIndex((a) => a.name === name)
    return j >= 0 ? { array: 'transaction.actions', list: txn, index: j } : null
  }, [name, form, formNo, model])
  const previous: ActionDef | null = located ? located.list[located.index] : null
  const read = previous ? actionToSpec(previous) : null
  const cols = ((form?.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[])
  const labelOf = (n: string) => {
    const c = cols.find((x) => [x.db_name, x.name, x.col_id].some((k) => String(k || '').toLowerCase() === n.toLowerCase()))
    return String(c?.heading || c?.name || n)
  }
  const hasDelete = forms.some((f) => (f.actions || []).map(legacyActionToDef)
    .some((a) => a.handler === 'system' && a.system_op === 'delete' && a.name !== name))

  const [spec, setSpec] = useState<ButtonSpec>(() => read ?? {
    name: '', does: 'fill', scope: 'form', field: '', value: '', when: null, confirm: null,
  })
  const [sources, setSources] = useState<BuildSources | null>(null)
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState(false)
  useEffect(() => {
    let cancelled = false
    transport.sources().then((s) => { if (!cancelled) setSources(s) })
      .catch(() => { if (!cancelled) setSources({ screens: [], reports: [] }) })
    return () => { cancelled = true }
  }, [transport])

  const set = (patch: Partial<ButtonSpec>) => { setSpec((s) => ({ ...s, ...patch })); setRefused(false) }
  const title = name ? `“${name}” button` : 'A new button'

  // A button the plain words cannot describe stays as it is.
  if (previous && !read) {
    return (
      <AnchoredPanel anchor={anchor} onClose={onClose} label={title} width={440}>
        <PanelHeader title={title} sub="Set up in the full designer" saved={false} />
        <div style={{ padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PanelNote>This button does more than these settings can show, so it is changed in the full designer (More actions › Open in the full designer).</PanelNote>
        </div>
      </AnchoredPanel>
    )
  }

  const screens = sources?.screens || []
  const reports = sources?.reports || []
  const editable = cols.filter((c) => !c.uda && !truthy(c.key) && !String(c.sql_expression ?? '').trim())
  const fieldOptions = editable.map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))
  const target = spec.does === 'open' ? screens.find((s) => s.obj_name === spec.target) : undefined
  // Fields of this record that pick a record of the target screen.
  const linkFields = target ? cols.filter((c) => parseLookup(c.lookup)?.table.toLowerCase() === target.table.toLowerCase()) : []
  const targetTitle = (t: string) => screens.find((s) => s.obj_name === t)?.title
    || reports.find((r) => r.obj_name === t)?.title || t
  const nameTaken = forms.some((f) => (f.actions || []).map(legacyActionToDef).some((a) => a.name === spec.name.trim() && a.name !== name))
  const ready = !!spec.name.trim() && !nameTaken && (
    spec.does === 'fill' ? !!spec.field && !!(spec.value || '').trim()
    : spec.does === 'message' ? !!(spec.message || '').trim()
    : spec.does === 'open' || spec.does === 'report' ? !!spec.target
    : true)

  const save = async () => {
    if (!ready) return
    const def = specToAction(spec, { objName, label: labelOf, previous })
    const array = located?.array || `${formBase(formNo)}.actions`
    const ops = located
      ? [
        { op: 'remove' as const, array, key: located.list[located.index].name },
        { op: 'insert' as const, array, item: def as unknown as Record<string, unknown>,
          ...(located.index + 1 < located.list.length ? { before: located.list[located.index + 1].name } : {}) },
      ]
      : [{ op: 'insert' as const, array, item: def as unknown as Record<string, unknown> }]
    setBusy(true)
    try {
      const ok = await session.apply({ set: [], reset: [], ops }, name ? `Changed the ${spec.name} button` : `Added the ${spec.name} button`)
      if (ok) onClose()
      else setRefused(true)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!located) return
    setBusy(true)
    try {
      const ok = await session.apply({ set: [], reset: [], ops: [{ op: 'remove', array: located.array, key: located.list[located.index].name }] },
        `Deleted the ${name} button`)
      if (ok) onClose()
      else setRefused(true)
    } finally {
      setBusy(false)
    }
  }

  const doesOptions = BUTTON_DOES.filter((d) => d.id !== 'delete' || !hasDelete || spec.does === 'delete')
  const writesCode = spec.does === 'fill' || spec.does === 'message'

  return (
    <AnchoredPanel anchor={anchor} onClose={onClose} label={title} width={460}>
      <PanelHeader title={title} sub={ready ? undefined : 'Fill in what it does'} saved={false} />
      <div style={{ padding: '12px 16px 6px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Name on the button</span>
          <input value={spec.name} autoFocus={!name} onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g. Submit" {...tw('field', 'make-changes-button-name')} style={inputStyle} />
          {nameTaken && <span style={{ fontSize: 13, color: '#b42318' }}>There is already a button with this name.</span>}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>What it does</span>
          <ChipGroup<ButtonDoes> label="What it does" hook="make-changes-button-does" value={spec.does}
            options={doesOptions.map((d) => ({ value: d.id, label: d.label }))}
            onChange={(does) => set({ does, target: '', match: null })} />
        </div>
        {spec.does === 'fill' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Set this field</span>
            <ChipGroup label="Set this field" hook="make-changes-button-field" value={spec.field} options={fieldOptions}
              onChange={(field) => set({ field })} />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13.5 }}>to</span>
              <input value={spec.value || ''} onChange={(e) => set({ value: e.target.value })} placeholder="e.g. Submitted"
                {...tw('field', 'make-changes-button-value')} style={inputStyle} />
            </label>
          </div>
        )}
        {spec.does === 'message' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>The message</span>
            <input value={spec.message || ''} onChange={(e) => set({ message: e.target.value })}
              placeholder="e.g. Claim {Claim no} is with Finance" {...tw('field', 'make-changes-button-message')} style={inputStyle} />
          </label>
        )}
        {writesCode && (
          <PanelNote>When you press Done, the AI writes the small piece of code behind this button from these words.</PanelNote>
        )}
        {spec.does === 'open' && (
          <>
            <PanelSelect label="Open which screen" value={spec.target || ''} onChange={(t) => {
              const scr = screens.find((s) => s.obj_name === t)
              const link = scr ? cols.find((c) => parseLookup(c.lookup)?.table.toLowerCase() === scr.table.toLowerCase()) : undefined
              set({ target: t, match: link && scr?.key_column ? { field: String(link.db_name || link.name), targetKey: scr.key_column } : null })
            }} options={[{ value: '', label: sources ? 'Choose a screen…' : 'Loading…' },
              ...screens.filter((s) => s.obj_name.toLowerCase() !== objName.toLowerCase()).map((s) => ({ value: s.obj_name, label: s.title }))]} />
            {target && (
              <PanelSelect label="On which record" value={spec.match?.field || ''}
                onChange={(f) => set({ match: f && target.key_column ? { field: f, targetKey: target.key_column } : null })}
                options={[{ value: '', label: 'A new record' },
                  ...linkFields.map((c) => ({ value: String(c.db_name || c.name), label: `The one in ${c.heading || c.name}` }))]}
                hint={linkFields.length ? undefined : `No field here is picked from ${target.title}, so it opens a new record.`} />
            )}
          </>
        )}
        {spec.does === 'report' && (
          <PanelSelect label="Print which document" value={spec.target || ''} onChange={(t) => set({ target: t })}
            options={[{ value: '', label: sources ? (reports.length ? 'Choose a document…' : 'This app has no printed documents yet') : 'Loading…' },
              ...reports.map((r) => ({ value: r.obj_name, label: r.title }))]} />
        )}
        {spec.does === 'send' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Send by</span>
            <ChipGroup label="Send by" hook="make-changes-button-channel" value={spec.channel || 'email'}
              options={[{ value: 'email', label: 'Email' }, { value: 'whatsapp', label: 'WhatsApp' }, { value: 'sms', label: 'Text message' }]}
              onChange={(channel) => set({ channel })} />
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>It opens a preview of the message before anything is sent.</span>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Where people see it</span>
          <ChipGroup label="Where people see it" hook="make-changes-button-scope" value={spec.scope}
            options={[{ value: 'form', label: 'Above the list and on the record' }, { value: 'row', label: 'On each record' }]}
            onChange={(scope) => set({ scope, ...(scope === 'form' ? { when: null } : {}) })} />
        </div>
        {spec.scope === 'row' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Show it only when</span>
            <ChipGroup label="Show it only when" hook="make-changes-button-when" value={spec.when?.field || ''}
              options={[{ value: '', label: 'Always' }, ...fieldOptions]}
              onChange={(field) => set({ when: field ? { field, value: spec.when?.value || '' } : null })} />
            {spec.when && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13.5 }}>is</span>
                <input value={spec.when.value} onChange={(e) => set({ when: { field: spec.when!.field, value: e.target.value } })}
                  placeholder="e.g. Draft" style={inputStyle} />
              </label>
            )}
          </div>
        )}
        <PanelRow title="Ask “Are you sure?” first" hint="People confirm before it runs">
          <Switch label="Ask before running" on={!!spec.confirm}
            onChange={(on) => set({ confirm: on ? (spec.confirm || `${spec.name || 'Run this'}?`) : null })} />
        </PanelRow>
        {spec.confirm !== null && spec.confirm !== undefined && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>The question</span>
            <input value={spec.confirm} onChange={(e) => set({ confirm: e.target.value })} style={inputStyle} />
          </label>
        )}
        <div style={{ fontSize: 14, color: VIOLET }} {...tw('field', 'make-changes-button-sentence')}>
          {buttonSentence(spec, labelOf, targetTitle)}
        </div>
        {refused && <PanelNote tone="warn">Not saved. {session.errors.join(' · ') || 'The change was refused.'}</PanelNote>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px 14px', borderTop: '1px solid var(--color-border, #f1f3f6)', marginTop: 6 }}>
        {located ? (
          <button type="button" onClick={() => { void remove() }} disabled={busy} {...tw('action', 'make-changes-delete-button')}
            style={{ border: 0, background: 'transparent', color: '#b42318', cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' }}>
            <Trash2 size={14} />Delete this button
          </button>
        ) : <span />}
        <VioletButton onClick={() => { void save() }} disabled={!ready || busy} hook="make-changes-button-done">
          {busy && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}Done
        </VioletButton>
      </div>
    </AnchoredPanel>
  )
}

const inputStyle: React.CSSProperties = {
  height: 34, boxSizing: 'border-box', width: '100%', borderRadius: 6, padding: '0 10px',
  border: '1px solid var(--color-border, #d5dae1)', fontSize: 14, fontFamily: 'inherit',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
}
